<script setup>
import { onBeforeUnmount, ref, watchEffect } from 'vue';
import CrossIcon from '@/Components/Icons/CrossIcon.vue';
import CheckIcon from '@/Components/Icons/CheckIcon.vue';

const deviceStatus = ref('');
const deviceLabel = ref('');
const isUnmounted = ref(false);
const currentWidth = ref(0);
const currentStream = ref();
const enabled = ref(false);

let javascriptNode = null;

const showBar = () => {
  let maxLevelL = 0.5;
  let oldLevelL = 0.1;
  if (javascriptNode) {
    javascriptNode.onaudioprocess = () => null;
  }

  const constraints = { audio: true, video: false };
  navigator.mediaDevices
    .getUserMedia(constraints)
    .then(async (stream) => {
      if (isUnmounted.value) {
        await stopLiveStream(stream);
        return;
      }
      currentStream.value = stream;
      deviceStatus.value = 'success';
      deviceLabel.value = stream?.getAudioTracks()[0]?.label;

      const audioContext = new AudioContext();
      const microphone = audioContext.createMediaStreamSource(stream);

      javascriptNode = audioContext.createScriptProcessor(1024, 1, 1);
      microphone.connect(javascriptNode);
      javascriptNode.connect(audioContext.destination);

      javascriptNode.onaudioprocess = (event) => {
        const inputL = event.inputBuffer.getChannelData(0);
        let instantL;
        let sumL = 0.0;
        for (let i = 0; i < inputL.length; ++i) {
          sumL += inputL[i] * inputL[i];
        }
        instantL = Math.sqrt(sumL / inputL.length);
        maxLevelL = Math.max(maxLevelL, instantL);
        instantL = Math.max(instantL, oldLevelL - 0.008);
        oldLevelL = instantL;
        currentWidth.value = Math.max(0, Math.min(100, (instantL / maxLevelL) * 100));
      };
    })
    .catch((err) => {
      console.error(err);
    });
};

try {
  showBar();
} catch (error) {
  console.error('Device start failed:', error);
  deviceStatus.value = 'fail';
}

async function stopLiveStream(stream) {
  await stream?.getTracks().forEach((track) => {
    track.stop();
    stream.removeTrack(track);
  });
}

watchEffect(() => {
  if (currentStream?.value?.active) {
    enabled.value = true;
  }
});

onBeforeUnmount(async () => {
  isUnmounted.value = true;
  if (currentStream.value) {
    await stopLiveStream(currentStream.value);
  }
});
</script>

<template>
  <div class="flex flex-col text-left">
    <div class="w-full inline-flex justify-between" style="z-index: 0">
      <label class="block text-sm font-medium text-gray-700 mb-1 pt-1">
        Microphone
        <span class="device-label">{{ deviceLabel }}</span>
      </label>
      <span v-if="deviceStatus === 'success'">
        <CheckIcon
          style="background-color: rgb(62 183 166)"
          class="absolute transition right-6 z-10 text-white rounded-full"
        />
      </span>
      <span v-else-if="deviceStatus === 'fail'">
        <CrossIcon class="absolute transition right-6 z-10 bg-red-100 text-red-800 rounded-full" />
      </span>
    </div>
    <div class="rounded overflow-hidden relative w-full h-0" style="padding-top: 8%">
      <div
        v-if="enabled"
        class="
          absolute
          bg-white
          rounded-lg
          h-4
          left-0
          right-0
          top-1/2
          transform
          -translate-y-1/2
          overflow-hidden
          border border-gray-300
        "
      >
        <div
          class="absolute bg-vice-teal-400 rounded-lg h-full top-0 left-0"
          :style="{ width: `${currentWidth}%` }"
        ></div>
      </div>
      <div
        v-if="!enabled"
        class="
          absolute
          top-0
          left-0
          w-full
          h-full
          flex flex-col
          items-center
          text-sm
          justify-center
          z-10
        "
      >
        <span class="flex items-center">
          <svg
            class="animate-spin -ml-1 h-8 w-8"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="#14B8A6"
              stroke-width="4"
            ></circle>
            <path
              class=""
              fill="#14B8A6"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>
